import axios, { AxiosError } from 'axios';
import type { ProblemDetail } from '@/types/api';
import { getToken } from '@/lib/auth-storage';
import { encerrarSessao } from '@/lib/sessao';

/**
 * Cliente HTTP central. Em dev, Vite faz proxy /api → backend (vite.config.ts).
 * Em produção, mesmo origem (frontend + backend atrás do Traefik).
 */
export const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json, application/problem+json',
  },
});

// Anexa Bearer automaticamente
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 → logout + redirect para /login
apiClient.interceptors.response.use(
  (resp) => resp,
  (error: AxiosError<ProblemDetail>) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      encerrarSessao();
    }
    return Promise.reject(error);
  },
);

/**
 * Extrai mensagem human-readable de erros do backend (ProblemDetail) ou
 * fallback genérico.
 */
export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError<ProblemDetail>(error)) {
    const data = error.response?.data;
    if (data) {
      if (data.violations && data.violations.length) {
        return data.violations.map((v) => `${v.field}: ${v.message}`).join('; ');
      }
      return data.detail || data.title || 'Erro inesperado';
    }
    return error.message;
  }
  return 'Erro inesperado';
}

/**
 * Extrai requestId da resposta de erro - útil para mostrar ao usuário em mensagens
 * de suporte.
 */
export function extractRequestId(error: unknown): string | undefined {
  if (axios.isAxiosError<ProblemDetail>(error)) {
    return error.response?.data?.requestId;
  }
  return undefined;
}

/**
 * Status HTTP do erro (ou undefined se não houver resposta - ex.: rede caiu).
 * Útil para distinguir erro do usuário (4xx) de erro de sistema (5xx).
 */
export function extractErrorStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}
